/**
 * Default cards of a maneuver brigade (R1-03 / R1-05, 2026-10-24, Dr. Tatar).
 *
 * Before: a brigade showed 5 battalion cards in Tab 1 (maneuver x3, artillery, engineers); the people left over up to the brigade
 * total (HQ, signal, intelligence, support battalion, reconnaissance) were hidden units made inside the calculator
 * (brigadeSupportStructure.js). Now the headquarters, the support battalion and the reconnaissance unit are cards like the others:
 * the planner sees them, resizes, retypes, moves or deletes them, and adds battalions as before. The brigade total is the sum
 * of the cards, so there is no hidden remainder.
 *
 * SIZES: SOURCED for the three US brigade combat team types, from the US Army MCoE Supplemental Manual 3-90 (Force Structure
 * Reference Data, Sept 2012, FMS data, Objective TOE); see docs/research/RESEARCH_BRIGADE_SIZES.md section 2. The unit figures add
 * up to the brigade totals (IBCT 4,468, ABCT 4,743, SBCT 4,479). Every other nation uses these as the NATO generic default
 * (no public establishment tables for Romania, Germany, France, Poland; decision of Dr. Tatar 2026-10-24). They are a reference,
 * not national data. The brigade engineer battalion figure includes its signal and military intelligence companies; the brigade
 * HQ card is the headquarters company (staff and its own support), with the HQ casualty profile.
 * Airborne, air assault, aviation, artillery, sustainment, logistics, reconnaissance and air defense have no sourced table: EDUCATED GUESS card lists
 * (see below). The engineer brigade (2,300) and the medical brigade (2,500) have EDUCATED GUESS card lists since 2026-10-25.
 *
 * role1: false = the card has no organic Role 1 of its own (Role 1 sits in the battalions: the US BCT headquarters has a surgeon section and no
 * treatment team; the brigade support battalion's medical company is the brigade's Role 2; docs/research/RESEARCH_BRIGADE_ROLE1.md).
 * position: 'BRIGADE' = at the brigade headquarters; 'SUPPORT_AREA' = the brigade support area (BRIGADE_SUPPORT_AREA_KM, never
 * closer to the front than the brigade); omitted = the typical position of the battalion type.
 */

// true since 2026-10-24 (approved by Dr. Tatar; Romania follows the generic cards). false: the brigade keeps its earlier cards and the hidden remainder.
export const BRIGADE_CARDS_ON = true;

export const BRIGADE_CARDS_STATUS = Object.freeze({
  sizes: 'SOURCED for the US brigade combat team types, NATO generic for other nations',
  basis: 'US Army MCoE SM 3-90 (2012) Objective TOE, FMS data; docs/research/RESEARCH_BRIGADE_SIZES.md'
});

const hq = (strength) => ({ unit_type: 'HQ', strength, mission: 'COMMAND', is_frontline: false, position: 'BRIGADE', role1: false, label: 'Brigade headquarters' });
const support = (strength) => ({ unit_type: 'LOGISTICS', strength, mission: 'SUSTAINMENT', is_frontline: false, position: 'SUPPORT_AREA', role1: false, label: 'Brigade support battalion' });
const recon = (strength) => ({ unit_type: 'RECONNAISSANCE', strength, mission: 'RECONNAISSANCE', label: 'Reconnaissance squadron' });

// ABCT: three combined arms battalions 635, fires 398, engineer 440, reconnaissance 429, support 1,386, HQ 185 = 4,743
const ABCT = (maneuverTypes) => [
  ...maneuverTypes.map((unit_type) => ({ unit_type, strength: 635 })),
  { unit_type: 'ARTILLERY', strength: 398 },
  { unit_type: 'ENGINEERS', strength: 440 },
  recon(429), support(1386), hq(185)
];
// IBCT: three infantry battalions 702, fires 408, engineer 418, reconnaissance 407, support 951, HQ 178 = 4,468
const IBCT = () => [
  { unit_type: 'INFANTRY', strength: 702 }, { unit_type: 'INFANTRY', strength: 702 }, { unit_type: 'INFANTRY', strength: 702 },
  { unit_type: 'ARTILLERY', strength: 408 },
  { unit_type: 'ENGINEERS', strength: 418 },
  recon(407), support(951), hq(178)
];
// SBCT: three infantry battalions 698, reconnaissance 405, fires 398, engineer 466, support 942, HQ 174 = 4,479
const SBCT = () => [
  { unit_type: 'INFANTRY', strength: 698 }, { unit_type: 'INFANTRY', strength: 698 }, { unit_type: 'INFANTRY', strength: 698 },
  recon(405),
  { unit_type: 'ARTILLERY', strength: 398 },
  { unit_type: 'ENGINEERS', strength: 466 },
  support(942), hq(174)
];

// Types without a sourced table (2026-10-24, Dr. Tatar: the brigade total is the sum of these cards). EDUCATED GUESS: airborne uses the
// infantry brigade set (the airborne brigades checked, 2nd BCT 82nd and 173rd, are 4,000 to 4,400); air assault the same set plus an
// aviation battalion (800, the card of the earlier build); aviation and artillery use the support unit sizes the tool already held for them
// (brigadeSupportStructure.js: aviation HQ 371 and support battalion 926, artillery HQ battery 192, signal 191, support battalion 769);
// sustainment keeps its battalions of the earlier build with an HQ of 200.
const signal = (strength) => ({ unit_type: 'SIGNAL', strength, mission: 'COMMUNICATIONS', is_frontline: false, position: 'BRIGADE', role1: false, label: 'Brigade signal company' });
const AIR_ASSAULT = () => {
  const cards = IBCT();
  cards.splice(3, 0, { unit_type: 'AVIATION', strength: 800 });   // after the three infantry battalions
  return cards;
};
// AVIATION: three aviation battalions 800, aviation support battalion 926, HQ company 371 = 3,697
const AVIATION = () => [
  { unit_type: 'AVIATION', strength: 800 }, { unit_type: 'AVIATION', strength: 800 }, { unit_type: 'AVIATION', strength: 800 },
  support(926), hq(371)
];
// ARTILLERY: three artillery battalions 700, HQ battery 192, signal 191, support battalion 769 = 3,252
const ARTILLERY = () => [
  { unit_type: 'ARTILLERY', strength: 700 }, { unit_type: 'ARTILLERY', strength: 700 }, { unit_type: 'ARTILLERY', strength: 700 },
  hq(192), signal(191), support(769)
];
// SUSTAINMENT is the US name of the logistics brigade (alias, 2026-10-25): it takes the LOGISTICS cards below (3,400); the earlier list of 2,450 is in docs/DEAD_CODE_REGISTER.md

// 2026-10-25 (Dr. Tatar: define the card lists of the types that still had a hidden remainder). The unit sizes are the ones the tool already held in the
// doctrinal composition of the type (unitTypes.js UNIT_TYPES[type].doctrinal_composition.brigade.sub_units), now shown as cards; EDUCATED GUESS, no public table.
// The brigade total is the sum of the cards (before: logistics 3,400 against 1,800 in cards, reconnaissance 5,000 against 3,080, air defense 5,000 against 1,380).
// LOGISTICS: two transport battalions 800, supply 600, maintenance 600, medical support 400, HQ 200 = 3,400
const LOGISTICS = () => [
  { unit_type: 'TRANSPORT', strength: 800 }, { unit_type: 'TRANSPORT', strength: 800 },
  { unit_type: 'SUPPLY', strength: 600 }, { unit_type: 'MAINTENANCE', strength: 600 }, { unit_type: 'MEDICAL_SUPPORT', strength: 400 },
  hq(200)
];
// RECONNAISSANCE: three reconnaissance squadrons 1,000, direct support artillery 500, support battalion 500, HQ troop 200 = 4,200
const RECONNAISSANCE = () => [
  { ...recon(1000) }, { ...recon(1000) }, { ...recon(1000) },
  { unit_type: 'ARTILLERY', strength: 500 },
  support(500), hq(200)
];
// AIR_DEFENSE: three air defense battalions 450, maintenance and supply battalion 250, HQ 150 = 1,750
const AIR_DEFENSE = () => [
  { unit_type: 'AIR_DEFENSE', strength: 450 }, { unit_type: 'AIR_DEFENSE', strength: 450 }, { unit_type: 'AIR_DEFENSE', strength: 450 },
  { unit_type: 'MAINTENANCE', strength: 250, mission: 'SUSTAINMENT', is_frontline: false, position: 'SUPPORT_AREA', role1: false, label: 'Brigade maintenance battalion' },
  hq(150)
];

// 2026-10-25 (Dr. Tatar approved: engineer and medical brigades get cards). Structure SOURCED: US Army FM 3-34 (2014) paragraphs 1-29, 1-30: the
// engineer brigade controls up to five engineer battalions, each with its own headquarters company and forward support company (no separate support
// card). Sizes EDUCATED GUESS: 130th Engineer Brigade about 1,600 with two battalions fits a headquarters of 200 and 700 per battalion
// (docs/research/RESEARCH_ENGINEER_MEDICAL_BRIGADE_CARDS.md). The earlier composition (700 x 3, logistics 400, headquarters 300 = 2,800) is in DEAD_CODE_REGISTER.md.
// ENGINEERS: three engineer battalions 700, HQ 200 = 2,300
const ENGINEERS = () => [
  { unit_type: 'ENGINEERS', strength: 700, mission: 'MOBILITY_SUPPORT' }, { unit_type: 'ENGINEERS', strength: 700, mission: 'MOBILITY_SUPPORT' },
  { unit_type: 'ENGINEERS', strength: 700, mission: 'MOBILITY_SUPPORT' },
  hq(200)
];
// MEDICAL: structure from FM 4-02 (hospital centers, multifunctional medical battalions; headquarters company about 65, secondary source);
// sizes EDUCATED GUESS: hospital center 800, two multifunctional medical battalions 800, HQ 100 = 2,500 (the tool's typical figure). They stand at the
// brigade position (rear, protected), carry no Role 1 (they are the providers) and the MEDICAL casualty profile (0.15).
const medicalCard = (label) => ({ unit_type: 'MEDICAL', strength: 800, mission: 'SUSTAINMENT', is_frontline: false, position: 'BRIGADE', role1: false, label });
const MEDICAL = () => [
  medicalCard('Hospital center'), medicalCard('Multifunctional medical battalion'), medicalCard('Multifunctional medical battalion'),
  hq(100)
];

export const BRIGADE_CARD_TEMPLATES = Object.freeze({
  MECHANIZED: ABCT(['MECHANIZED', 'MECHANIZED', 'ARMOR']),
  ARMOR:      ABCT(['ARMOR', 'ARMOR', 'MECHANIZED']),
  HEAVY:      ABCT(['ARMOR', 'ARMOR', 'MECHANIZED']),
  INFANTRY:   IBCT(),
  MOUNTAIN:   IBCT(),
  STRYKER:    SBCT(),
  AIRBORNE:   IBCT(),
  AIR_ASSAULT: AIR_ASSAULT(),
  AVIATION:   AVIATION(),
  ARTILLERY:  ARTILLERY(),
  LOGISTICS: LOGISTICS(),
  RECONNAISSANCE: RECONNAISSANCE(),
  AIR_DEFENSE: AIR_DEFENSE(),
  ENGINEERS: ENGINEERS(),
  MEDICAL: MEDICAL()
});

/**
 * The cards of a brigade type (a copy the builder can use), or null when the type has no card list (the caller keeps the
 * earlier battalions). The brigade total is the sum of the cards: a total typed in the quick build is not used.
 */
export function brigadeCardsFor(brigadeType) {
  const template = BRIGADE_CARD_TEMPLATES[brigadeType];
  return template ? template.map((c) => ({ ...c })) : null;
}

export function brigadeCardsTotal(cards) {
  return cards.reduce((sum, c) => sum + c.strength, 0);
}
